/** Основа баланса: версия, кривые цен, статы, атаки. */
import { FIXED_ONE, HeroId, PosInt, UInt } from "../common";
import { z } from "zod";

/** Версия набора данных баланса: `YYYY-MM-DD.N` (например, `2026-10-24.1`). */
export const BalanceVersion = z
  .string()
  .regex(/^(beta-)?\d{4}-\d{2}-\d{2}\.\d+$/, "balance version: [beta-]YYYY-MM-DD.N");
export type BalanceVersion = z.infer<typeof BalanceVersion>;

/**
 * Кривая цены по уровню: `cost(level) = base · (1000 + growth)^level / 1000^level`, округление вниз.
 * level — текущий уровень (покупка уровня level → level + 1). Считает engine (E5), здесь только параметры.
 */
export const CostCurve = z.object({
  /** Цена первого шага, монет. */
  base: PosInt,
  /** Рост за уровень, промилле (150 = +15 % за уровень). */
  growthPermille: UInt,
});
export type CostCurve = z.infer<typeof CostCurve>;

/** Характеристика, которую меняют усиления, звёзды и Tech. Список расширяется MR в contracts. */
export const StatId = z.enum([
  "damage",
  "fireRate",
  "range",
  "critChance",
  "critDamage",
  "projectileCount",
  "skillCooldown",
  "skillPower",
  "coinGain",
  "hqMaxHp",
  "hqRegen",
  "cannonDamage",
  "cannonFireRate",
  "zombieSpeed",
  /** Поджог: урон горения за секунду на цели (горит 3 с, попадание обновляет); `add` — единицы урона. */
  "burnDamage",
  /** Рикошет: сколько раз снаряд перескакивает на следующую цель после попадания; `add` — штуки. */
  "ricochetCount",
]);
export type StatId = z.infer<typeof StatId>;

/**
 * Изменение характеристики. `add` — прибавка в единицах характеристики, `mulPermille` — множитель в промилле
 * (+200 = ×1,2). Порядок применения: все `add`, затем произведение всех `mulPermille`.
 */
export const StatModifier = z.object({
  stat: StatId,
  op: z.enum(["add", "mulPermille"]),
  value: z.number().int(),
  /** Кого касается: все герои, один герой или штаб. Нет поля — все герои. */
  target: z.union([z.literal("allHeroes"), z.literal("hq"), HeroId]).optional(),
});
export type StatModifier = z.infer<typeof StatModifier>;

/** Вид снаряда: определяет спрайт и эффект попадания. */
export const ProjectileKind = z.enum(["bullet", "bomb", "spark", "flame", "frost", "cannonball"]);
export type ProjectileKind = z.infer<typeof ProjectileKind>;

/** Боевые параметры атакующего (герой, пушка штаба). */
export const AttackDef = z.object({
  /** Урон одного попадания. */
  damage: PosInt,
  /** Пауза между выстрелами, тиков. */
  cooldownTicks: PosInt,
  /** Дальность, единиц фиксированной точки. */
  range: z
    .number()
    .int()
    .positive()
    .max(40 * FIXED_ONE),
  /** Скорость снаряда, единиц за тик; 0 — мгновенное попадание (молния). */
  projectileSpeed: UInt,
  projectile: ProjectileKind,
  /** Радиус урона по площади, единиц; 0 — одна цель. */
  splashRadius: UInt.default(0),
  /** Врождённый шанс крита героя, промилле (нет — 0): снайпер беты. */
  baseCritChance: z.number().int().min(0).max(1000).optional(),
  /** Врождённый множитель крита, промилле (нет — 1500). */
  baseCritDamage: z.number().int().min(1000).optional(),
});
export type AttackDef = z.infer<typeof AttackDef>;
